import React, { useContext, useMemo } from 'react';
import { connect } from 'react-redux';

import FLAG from 'config/flag.singleton';
import PERM from 'constants/core/perm.const';
import {
  selectAccessLockToken,
  selectAccessPerms,
  selectAccessSalonId,
  selectAccessStylistId,
  selectAccessUserId,
} from 'selectors/access.selectors';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import fromObjectToValues from 'utils/converters/object/fromObjectToValues.util';
import alwaysSame from 'utils/functions/nullary/alwaysSame.util';
import logger from 'utils/logger.util';
import isMissing from 'utils/predicates/any/isMissing.util';
import isEnumVal from 'utils/predicates/enum/isEnumVal.util';

const L = logger('AccessProvider');

const USE_SPAM_LOG = fromAnyToBool(FLAG.loggerSpam);

// noinspection JSCheckFunctionSignatures
const Context = React.createContext();

const PERMS = new Set(fromObjectToValues(PERM));

class FilteredPerms extends Map {
  constructor(allowed) {
    super();

    const set = Map.prototype.set;

    if (isMissing(allowed)) return;

    if (!Array.isArray(allowed)) {
      L.warn('FilteredPerms()', 'Invalid array allowed:', allowed);
      return;
    }

    for (const p of PERMS) {
      if (!allowed.includes(p)) continue;
      set.call(this, p, p);
      set.call(this, p.index, p);
      set.call(this, fromAnyToTrimmed(p.index), p);
      set.call(this, p.snake, p);
      set.call(this, p.camel, p);
    }
  }

  set(key, value) {
    throw new TypeError('Perms.prototype.set is not allowed to be used');
  }

  delete(key) {
    throw new TypeError('Perms.prototype.delete is not allowed to be used');
  }
}

const Provider = ({ stylistId, salonId, userId, perms: permsFromProps, children }) => {
  const perms = useMemo(() => new FilteredPerms(permsFromProps), [permsFromProps]);
  const ids = useMemo(() => ({ stylistId, salonId, userId }), [salonId, stylistId, userId]);

  const value = useMemo(() => ({ perms, ids }), [ids, perms]);

  L.debug('Provider()', permsFromProps, '->', value);

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const AccessProvider = connect((state) => ({
  lt: selectAccessLockToken(state),
  perms: selectAccessPerms(state),
  stylistId: selectAccessStylistId(state),
  salonId: selectAccessSalonId(state),
  userId: selectAccessUserId(state),
}))(Provider);

export const useCustomPermission = (permission) => {
  const value = useContext(Context)?.perms;

  if (!value) L.error('useCustomPermission()', 'Please check if your component is under AccessProvider');

  if (!value) L.error('useCustomPermission()', 'Please check if your component is under AccessProvider');

  const found = value.get(permission);
  const result = fromAnyToBool(found);

  if (USE_SPAM_LOG) L.debug('useCustomPermission()', result, '<-', found, '<-', permission);

  return result;
};

export const useCustomPermissions = (operator, permissions) => {
  const value = useContext(Context)?.perms;

  const count = Array.isArray(permissions) && permissions.filter(alwaysSame).length;

  if (count && 'any' === operator) {
    for (const p of permissions) {
      const found = value.get(p);
      if (found) {
        if (USE_SPAM_LOG) L.debug('useCustomPermissions()', operator, true, '<-', found, '<-', p, '/', permissions);
        return true;
      }
    }
    if (USE_SPAM_LOG) L.debug('useCustomPermissions()', operator, false, '<-', void 1, '<-', permissions);
    return false;
  }

  if (count && 'all' === operator) {
    for (const p of permissions) {
      const found = value.get(p);
      if (!found) {
        if (USE_SPAM_LOG) L.debug('useCustomPermissions()', operator, false, '<-', found, '<-', p, '/', permissions);
        return false;
      }
    }
    if (USE_SPAM_LOG) L.debug('useCustomPermissions()', operator, true, '<-', void 1, '<-', permissions);
    return true;
  }

  L.warn('useCustomPermissions()', 'Not using an array for permissions:', permissions);

  if (!value) L.error('useCustomPermission()', 'Please check if your component is under AccessProvider');

  const found = value.get(permissions);
  const result = fromAnyToBool(found);
  if (USE_SPAM_LOG) L.debug('useCustomPermissions()', operator, result, '<-', found, '<-', permissions);

  return result;
};

export const useCustomPermissionRegistrationCheck = (permission, tag) => {
  if (PERM[permission] || isEnumVal(PERM, permission)) return;

  const t = fromAnyToTrimmed(tag);
  L.error('useCustomPermissionRegistrationCheck()', t, 'Permission:', permission, 'is not hardcoded');
};

export const useAccessIds = () => {
  const value = useContext(Context)?.ids;

  const { salonId, stylistId, userId } = value ?? {};

  return useMemo(
    () => ({
      accessSalonId: salonId,
      accessStylistId: stylistId,
      accessUserId: userId,
    }),
    [salonId, stylistId, userId],
  );
};
